import type { ReactNode } from 'react';
import { Button } from '../../atoms/Button/Button';
import styles from './ModalActions.module.css';

/**
 * Figma: `_Modal actions` (2013:10520). The `_` marks it internal; `Modal`
 * composes it.
 *
 * `Type` becomes `layout`:
 *
 * - `Horizontal fill container` (2013:10545) → `'fill'`: the buttons split the
 *   width between them.
 * - `Horizontal right aligned (checkbox)` (2013:10569) → `'end'`: the buttons
 *   hug their labels at the right, and a `leading` slot sits at the left. The
 *   variant's name promises a checkbox there; what it draws is a `Ghost Color`
 *   button reading "More info". Either fits the slot, so it is a `ReactNode`.
 *
 * `Tertiary button` (toggle) → `cancelLabel`. Absent means no cancel button.
 */
export type ModalActionsLayout = 'fill' | 'end';

export type ModalActionsProps = {
  layout?: ModalActionsLayout;
  confirmLabel?: string;
  onConfirm?: () => void;
  /** Swaps the confirm button to `Hierarchy=Destructive`. */
  destructive?: boolean;
  confirmDisabled?: boolean;
  cancelLabel?: string;
  onCancel?: () => void;
  /** Left-hand content in the `'end'` layout. Ignored by `'fill'`, which has none. */
  leading?: ReactNode;
  className?: string;
};

export function ModalActions({
  layout = 'fill',
  confirmLabel,
  onConfirm,
  destructive = false,
  confirmDisabled = false,
  cancelLabel,
  onCancel,
  leading,
  className,
}: ModalActionsProps) {
  const fill = layout === 'fill';
  const buttonClass = fill ? styles.fillButton : undefined;

  const buttons = (
    <>
      {cancelLabel !== undefined && (
        <Button hierarchy="tertiary" onClick={onCancel} className={buttonClass}>
          {cancelLabel}
        </Button>
      )}
      {confirmLabel !== undefined && (
        <Button
          hierarchy={destructive ? 'destructive' : 'primary'}
          onClick={onConfirm}
          disabled={confirmDisabled}
          className={buttonClass}
        >
          {confirmLabel}
        </Button>
      )}
    </>
  );

  return (
    <div className={[styles.actions, className].filter(Boolean).join(' ')}>
      <div className={[styles.content, fill ? null : styles.contentEnd].filter(Boolean).join(' ')}>
        {fill ? (
          buttons
        ) : (
          <>
            {leading}
            <div className={styles.end}>{buttons}</div>
          </>
        )}
      </div>
    </div>
  );
}
